/**
 * 碰撞检测器。
 * 职责：
 * 1. 仅消费外部已构建完成的 `boundsTree` 进行拖拽碰撞检测；
 * 2. 使用现有加速树的几何相交能力进行真实碰撞检测；
 * 3. 提供碰撞命中可视化调试；
 * 4. 不负责 BVH 的创建、更新或销毁。
 */

import * as THREE from 'three';
import type { Viewer } from '../../viewer/Viewer';
import { ImportedModelMeshIdentityResolver } from '../../imported-model/selection/ImportedModelMeshIdentityResolver';

/**
 * 碰撞检测使用的加速树最小协议。
 *
 * 设计说明：
 * 1. 拖拽碰撞只需要消费 `intersectsGeometry()` 能力，不需要依赖 BVH 具体实现类；
 * 2. 这里显式声明最小协议，避免拖拽层把 `three-mesh-bvh` 的构建细节继续耦合进来；
 * 3. 这样可以清晰表达本类只负责“计算碰撞”，不负责“生成加速结构”。
 */
interface ICollisionAccelerationTree {
    /**
     * 判断当前几何与目标几何在给定变换下是否相交。
     *
     * @param geometry 目标几何体。
     * @param matrix 当前几何空间到目标几何空间的变换矩阵。
     * @returns 是否相交。
     */
    intersectsGeometry(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): boolean;
}

/**
 * 网格碰撞缓存条目。
 */
interface ICollisionMeshEntry {
    /** 参与碰撞检测的网格。 */
    mesh: THREE.Mesh;
    /** 网格几何体。 */
    geometry: THREE.BufferGeometry;
    /** 当前几何体可用的碰撞加速树协议。 */
    accelerationTree: ICollisionAccelerationTree;
}

/**
 * BVH helper 调试条目。
 */
/**
 * 调试盒类型。
 */
enum DebugHitBoxType {
    /** 拖拽对象命中盒。 */
    Drag = 'drag-hit-box',
    /** 场景对象命中盒。 */
    Scene = 'scene-hit-box',
}

/**
 * 基于现有加速树的拖拽碰撞检测器。
 */
export class CollisionDetector {
    /** 拖拽对象中的所有可检测网格。 */
    private dragMeshEntries: ICollisionMeshEntry[] = [];

    /** 场景对象中的所有可检测网格。 */
    private sceneMeshEntries: ICollisionMeshEntry[] = [];

    /** 调试模式开关。 */
    private debugEnabled = true;

    /** 渲染器引用。 */
    private renderer: Viewer | null = null;

    /** 调试创建的命中盒列表。 */
    private debugBoxes: THREE.Mesh[] = [];

    /** 为避免高频分配复用的包围盒。 */
    private readonly dragWorldBox = new THREE.Box3();

    /** 为避免高频分配复用的包围盒。 */
    private readonly sceneWorldBox = new THREE.Box3();

    /** A 空间到 B 空间的临时矩阵。 */
    private readonly dragToSceneMatrix = new THREE.Matrix4();

    
    /**
     * 从几何体上解析可用于拖拽碰撞的最小加速树协议。
     *
     * 设计说明：
     * 1. `three-mesh-bvh` 会给 `BufferGeometry` 注入更完整的 `boundsTree` 类型；
     * 2. 拖拽碰撞层只需要 `intersectsGeometry()` 这一最小能力，不应该覆写几何体原始扩展类型；
     * 3. 因此这里把外部树结构收口为最小协议并缓存到条目里，避免本模块和 BVH 具体类型发生冲突。
     *
     * @param geometry 目标几何体。
     * @returns 碰撞加速树；不存在或不满足协议时返回 `null`。
     */
    private resolveCollisionAccelerationTree(geometry: THREE.BufferGeometry): ICollisionAccelerationTree | null {
        const candidateAccelerationTree = Reflect.get(geometry, 'boundsTree');
        if (!candidateAccelerationTree || typeof candidateAccelerationTree !== 'object') {
            return null;
        }

        const collisionAccelerationTree = candidateAccelerationTree as Partial<ICollisionAccelerationTree>;
        if (typeof collisionAccelerationTree.intersectsGeometry !== 'function') {
            return null;
        }

        return collisionAccelerationTree as ICollisionAccelerationTree;
    }
    

    /**
     * 初始化碰撞检测器。
     *
     * @param componentObject 被拖拽对象或对象组。
     * @param sceneObjects 参与碰撞的场景对象集合。
     * @param renderer 渲染器引用。
     */
    public init(
        componentObject: THREE.Object3D | readonly THREE.Object3D[] | null,
        sceneObjects: THREE.Object3D[],
        renderer?: Viewer,
    ): void {
        this.dispose();
        this.renderer = renderer || null;

        if (!componentObject) {
            return;
        }

        const componentObjects = Array.isArray(componentObject) ? componentObject : [componentObject];
        componentObjects.forEach((object) => {
            const entries = this.collectCollisionMeshEntries(object);
            this.dragMeshEntries.push(...entries);
        });

        const sceneObjectMap = new Map<string, THREE.Object3D>();
        sceneObjects.forEach((sceneObject) => {
            sceneObjectMap.set(sceneObject.uuid, sceneObject);
        });

        sceneObjectMap.forEach((sceneObject) => {
            const entries = this.collectCollisionMeshEntries(sceneObject);
            this.sceneMeshEntries.push(...entries);
        });
    }

    /**
     * 设置调试模式。
     *
     * @param enabled 是否启用调试模式。
     */
    public setDebugEnabled(enabled: boolean): void {
        this.debugEnabled = enabled;
        if (!enabled) {
            this.clearDebugBoxes();
        }
    }

    /**
     * 执行拖拽碰撞检测。
     *
     * @param componentObject 当前拖拽对象。
     * @returns 是否发生碰撞。
     */
    public detect(componentObject: THREE.Object3D | null): boolean {
        if (!componentObject || this.dragMeshEntries.length === 0 || this.sceneMeshEntries.length === 0) {
            return false;
        }

        if (this.debugEnabled) {
            this.clearDebugBoxes();
        }

        for (const dragEntry of this.dragMeshEntries) {
            for (const sceneEntry of this.sceneMeshEntries) {
                if (dragEntry.mesh === sceneEntry.mesh) {
                    continue;
                }

                if (this.intersectsMeshPair(dragEntry, sceneEntry)) {
                    if (this.debugEnabled) {
                        this.createDebugHitBoxes(dragEntry.mesh, sceneEntry.mesh);
                    }
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * 释放碰撞检测资源。
     */
    public dispose(): void {
        this.clearDebugBoxes();
        this.dragMeshEntries = [];
        this.sceneMeshEntries = [];
        this.renderer = null;
    }

    /**
    * 收集对象树中的所有碰撞网格。
    * 说明：
    * 1. 仅消费已有 `boundsTree`，禁止在拖拽流程中创建任何加速结构；
    * 2. 仅特征结果体参与碰撞检测，排除基准面与辅助对象；
    * 3. 未接入 `raycast-acceleration` 的几何体会被直接跳过，由上游统一负责 BVH 生命周期。
     *
     * @param object 根对象。
     * @returns 网格缓存条目集合。
     */
    private collectCollisionMeshEntries(object: THREE.Object3D): ICollisionMeshEntry[] {
        const entries: ICollisionMeshEntry[] = [];
        const visitedMeshIds = new Set<string>();
        const candidateRoots: THREE.Object3D[] = [
            object,
            ...(this.renderer?.listImportedModelInstancedSourceMeshes?.(object) ?? []),
        ];

        candidateRoots.forEach((candidateRoot) => candidateRoot.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) {
                return;
            }
            if (visitedMeshIds.has(child.uuid)) {
                return;
            }
            visitedMeshIds.add(child.uuid);

            // 拖拽碰撞仅对真实几何对象生效：
            // 1. 特征结果体继续保留；
            // 2. imported-model 场景中的真实 mesh 也允许参与；
            // 3. overlay、高亮与辅助对象仍然排除在外。
            const isFeatureResultMesh = Reflect.get(child, 'isFeatureResultMesh') === true;
            const isImportedModelMesh = ImportedModelMeshIdentityResolver.isImportedVisibleMesh(child);
            if (!isFeatureResultMesh && !isImportedModelMesh) {
                return;
            }
            if (child.userData?.skipSelection || child.userData?.isSelectionHighlight || child.userData?.isImportedModelOverlay) {
                return;
            }

            const geometry = child.geometry;
            if (!(geometry instanceof THREE.BufferGeometry)) {
                return;
            }

            const accelerationTree = this.resolveCollisionAccelerationTree(geometry);
            if (!accelerationTree) {
                return;
            }

            this.renderer?.prepareImportedModelSourceMeshForSpatialRead?.(child);
            entries.push({ mesh: child, geometry, accelerationTree });
        }));

        return entries;
    }

    /**
     * 检测两网格是否发生碰撞。
     * 先做世界包围盒粗筛，再进行现有加速树的几何相交精判。
     *
     * @param dragEntry 拖拽网格条目。
     * @param sceneEntry 场景网格条目。
     * @returns 是否碰撞。
     */
    private intersectsMeshPair(dragEntry: ICollisionMeshEntry, sceneEntry: ICollisionMeshEntry): boolean {
        const dragMesh = dragEntry.mesh;
        const sceneMesh = sceneEntry.mesh;
        const sceneGeometry = sceneEntry.geometry;
        const dragAccelerationTree = dragEntry.accelerationTree;

        if (!this.renderer?.prepareImportedModelSourceMeshForSpatialRead?.(dragMesh)) {
            dragMesh.updateMatrixWorld(true);
        }
        if (!this.renderer?.prepareImportedModelSourceMeshForSpatialRead?.(sceneMesh)) {
            sceneMesh.updateMatrixWorld(true);
        }

        this.dragWorldBox.setFromObject(dragMesh);
        this.sceneWorldBox.setFromObject(sceneMesh);

        if (!this.dragWorldBox.intersectsBox(this.sceneWorldBox)) {
            return false;
        }

        this.dragToSceneMatrix.copy(dragMesh.matrixWorld).invert().multiply(sceneMesh.matrixWorld);
        return dragAccelerationTree.intersectsGeometry(sceneGeometry, this.dragToSceneMatrix);
    }

    /**
     * 创建碰撞命中调试盒。
     *
     * @param dragMesh 发生碰撞的拖拽网格。
     * @param sceneMesh 发生碰撞的场景网格。
     */
    private createDebugHitBoxes(dragMesh: THREE.Mesh, sceneMesh: THREE.Mesh): void {
        if (!this.renderer?.scene) {
            return;
        }

        this.dragWorldBox.setFromObject(dragMesh);
        this.sceneWorldBox.setFromObject(sceneMesh);

        this.createDebugBoxFromWorldBox(this.dragWorldBox, 0x3b82f6, DebugHitBoxType.Drag);
        this.createDebugBoxFromWorldBox(this.sceneWorldBox, 0xf59e0b, DebugHitBoxType.Scene);
    }

    /**
     * 从世界包围盒创建调试线框盒。
     *
     * @param worldBox 世界包围盒。
     * @param color 颜色。
     * @param debugType 调试盒类型。
     */
    private createDebugBoxFromWorldBox(worldBox: THREE.Box3, color: number, debugType: DebugHitBoxType): void {
        if (!this.renderer?.scene) {
            return;
        }

        const center = worldBox.getCenter(new THREE.Vector3());
        const size = worldBox.getSize(new THREE.Vector3());

        const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
        const material = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.28,
            wireframe: true,
            depthTest: false,
            depthWrite: false,
        });

        const debugBox = new THREE.Mesh(geometry, material);
        debugBox.name = `CollisionDebugBox_${debugType}`;
        debugBox.renderOrder = 998;
        debugBox.position.copy(center);
        (debugBox as unknown as { ignore?: boolean }).ignore = true;

        this.renderer.scene.add(debugBox);
        this.debugBoxes.push(debugBox);
    }

    /**
     * 清理调试命中盒。
     */
    private clearDebugBoxes(): void {
        this.debugBoxes.forEach((box) => {
            this.renderer?.scene?.remove(box);
            box.geometry.dispose();
            if (box.material instanceof THREE.Material) {
                box.material.dispose();
            }
        });
        this.debugBoxes = [];
    }

}
